import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteAdminNotification,
  getUnreadCount,
  listAdminNotifications,
  markNotificationRead,
  markNotificationUnread,
  type AdminNotificationFilters,
} from '../../../api/admin-notifications';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

const NOTIFICATIONS_KEY = ['admin-notifications'] as const;
const UNREAD_COUNT_KEY = ['admin-notifications', 'unread-count'] as const;

export function useAdminNotifications(filters: AdminNotificationFilters) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['admin-notifications', filters],
    queryFn: () => listAdminNotifications(filters, getToken()),
  });
}

export function useUnreadNotificationCount() {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => getUnreadCount(getToken()),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
}

export function useMarkNotificationRead() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id, getToken()),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useMarkNotificationUnread() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markNotificationUnread(id, getToken()),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDeleteAdminNotification() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAdminNotification(id, getToken()),
    onSuccess: () => invalidateAll(queryClient),
  });
}
